import { useAnalytics } from '@repo/analytics/analytics-provider';
import { Button } from '@repo/design-system/components/ds/button';
import { FlexColumn } from '@repo/design-system/components/layout/flex-column';
import { toast } from '@repo/design-system/components/ui/toaster';
import { Type } from '@repo/design-system/components/ui/type';
import { useHostType } from '@repo/host/hooks/use-host-info';
import { useT } from '@repo/i18n/components/i18n.client';
import { SHARE_STATE_TOKEN_QUERY_PARAM } from '@repo/map-user-data/share-state';
import { css } from '@repo/panda/css';
import { useCreateSavedChecklistMutation } from '@repo/store/maps/api/mapsApi';
import { useAppDispatch, useAppSelector } from '@repo/store/maps/hooks';
import { selectAuthToken } from '@repo/store/maps/slices/auth';
import { selectSharedChecklistPayload, setActiveCustomChecklistId, setSharedChecklistPayload, setSidebarView } from '@repo/store/maps/slices/clientContext';
import { selectExternalMapId, selectMapData, selectSteamId, selectTitleName } from '@repo/store/maps/slices/mapData';
import type { MapDataLocationFeature } from '@repo/types/maps/map-types';
import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { checklistHeroIconClass, useChecklistHero } from './checklist-hero';
import { PinListSidebarShell } from './pin-list-sidebar-shell';
interface SharedChecklistSidebarViewProps {
  onBack: () => void;
  isMobilePane?: boolean;
  onSearchFocus?: () => void;
}
function stripChecklistParamFromUrl() {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  if (!url.searchParams.has('cl') && !url.searchParams.has(SHARE_STATE_TOKEN_QUERY_PARAM)) return;
  url.searchParams.delete('cl');
  url.searchParams.delete(SHARE_STATE_TOKEN_QUERY_PARAM);
  window.history.replaceState(null, '', url.toString());
}
export const SharedChecklistSidebarView: React.FC<SharedChecklistSidebarViewProps> = ({
  onBack,
  isMobilePane = false,
  onSearchFocus
}) => {
  const {
    t
  } = useT(['game_map']);
  const mapData = useAppSelector(selectMapData);
  const externalMapId = useAppSelector(selectExternalMapId);
  const titleName = useAppSelector(selectTitleName);
  const mapName = mapData?.metadata.name;
  const steamId = useAppSelector(selectSteamId);
  const payload = useAppSelector(selectSharedChecklistPayload);
  const isLoggedIn = useAppSelector(state => state.auth.isAuthenticated);
  const token = useAppSelector(selectAuthToken);
  const dispatch = useAppDispatch();
  const [createChecklist, {
    isLoading: isSaving
  }] = useCreateSavedChecklistMutation();
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const analytics = useAnalytics();
  const hostType = useHostType();
  useEffect(() => {
    if (!payload) onBack();
  }, [payload, onBack]);
  const pinFeatures = useMemo<MapDataLocationFeature[]>(() => {
    if (!payload || !mapData) return [];
    const wanted = new Set(payload.pinIds);
    return mapData.geojson.features.filter((feature): feature is MapDataLocationFeature => feature.geometry.type === 'Point' && wanted.has(feature.properties.externalId));
  }, [payload, mapData]);
  const {
    backgroundImageUrl,
    pinColor,
    iconChar,
    iconFontStyle
  } = useChecklistHero(pinFeatures, mapData, steamId);
  const handleSave = async () => {
    if (!payload) return;
    if (!isLoggedIn || !token) {
      setIsLoginModalOpen(true);
      return;
    }
    if (!externalMapId) return;
    try {
      const created = await createChecklist({
        mapExternalId: externalMapId,
        input: {
          title: payload.title,
          description: payload.description,
          pinIds: payload.pinIds
        },
        token
      }).unwrap();
      toast({
        description: t($_0 => $_0.save_checklist.checklist_saved)
      });
      if (analytics && hostType) {
        analytics.event('game_map_shared_checklist_save', {
          mapId: externalMapId,
          mapName,
          titleName,
          pinCount: payload.pinIds.length,
          platform: hostType
        });
      }
      stripChecklistParamFromUrl();
      dispatch(setSharedChecklistPayload(null));
      dispatch(setActiveCustomChecklistId(created.id));
      dispatch(setSidebarView('customChecklist'));
    } catch {
      toast({
        description: t($ => $.save_checklist.couldnt_save_checklist),
        variant: 'error'
      });
    }
  };
  if (!payload) return null;
  const hero = <FlexColumn align="center" gap="12" width="full" data-drawer-drag="true">
            <div className={checklistHeroIconClass} style={{
      backgroundColor: pinColor,
      ...iconFontStyle
    }}>
                {iconChar}
            </div>
            <Type variant="body-xs" color="brand.primary" className={css({
      textAlign: 'center'
    })}>
                {t($_1 => $_1.save_checklist.shared_with_you)}
            </Type>
            <FlexColumn align="center" gap="4" width="full">
                <Type variant="header-m" color="white" className={css({
        textAlign: 'center',
        wordBreak: 'break-word'
      })} title={payload.title}>
                    {payload.title}
                </Type>
                {payload.description && <Type variant="body-s" color="text.secondary" className={css({
        textAlign: 'center',
        wordBreak: 'break-word'
      })}>
                        {payload.description}
                    </Type>}
            </FlexColumn>
        </FlexColumn>;
  const headerAction = <Button onClick={handleSave} color="primary" size="md" disabled={isSaving}>
            {t($_2 => $_2.save_checklist.save)}
        </Button>;
  return <PinListSidebarShell backgroundImageUrl={backgroundImageUrl} isMobilePane={isMobilePane} onBack={onBack} onSearchFocus={onSearchFocus} headerAction={headerAction} pinFeatures={pinFeatures} hero={hero} disableSearch readOnly selectSource="sidebar_checklist" boundsFitKey="shared" isLoginModalOpen={isLoginModalOpen} onLoginModalOpenChange={setIsLoginModalOpen} />;
};